import Phaser from "phaser";
import { COLORS, ENGULF_PAD } from "../config";
import type PlayerCell from "./PlayerCell";
import type { EnemyTier, PathogenDef } from "../types";

/** Emitted when a pathogen's removal animation (digest or pop) completes. */
export const PATHOGEN_GONE = "pathogen-gone";

/**
 * A STATIC enemy fixed in place. Shows a number = its Strength (engulf mode) or
 * HP (shoot mode). In engulf mode it also pulses red (don't eat — ≥ you) or
 * green (safe meal in range). Removed by being engulfed (eaten) or popped (shot).
 */
export default class Pathogen extends Phaser.GameObjects.Sprite {
  readonly def: PathogenDef;
  readonly tier: EnemyTier;
  readonly points: number; // original value (for scoring)
  value: number; // displayed number; decremented by shots in shoot mode
  removed = false;

  private label: Phaser.GameObjects.Text;
  private indicator?: Phaser.GameObjects.Sprite;
  private player?: PlayerCell;
  private showSafety: boolean;
  private spinDir: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    def: PathogenDef,
    value: number,
    opts: { tier?: EnemyTier; player?: PlayerCell; showSafety?: boolean } = {},
  ) {
    super(scene, x, y, def.kind);
    this.def = def;
    this.value = Math.max(1, Math.round(value));
    this.points = this.value;
    this.tier = opts.tier ?? "standard";
    this.player = opts.player;
    this.showSafety = opts.showSafety ?? false;
    this.spinDir = Math.random() < 0.5 ? 1 : -1;

    scene.add.existing(this);
    this.setDepth(5);

    if (this.showSafety) {
      this.indicator = scene.add
        .sprite(x, y, "soft")
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(4)
        .setVisible(false);
    }

    const fontSize = Math.max(20, Math.round(def.radius * 0.95));
    this.label = scene.add
      .text(x, y, `${this.value}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: `${fontSize}px`,
        color: "#ffffff",
        fontStyle: "bold",
        stroke: "#0a1020",
        strokeThickness: Math.max(4, fontSize * 0.22),
      })
      .setOrigin(0.5)
      .setDepth(7);
  }

  preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    if (this.removed) return;

    this.rotation += this.spinDir * delta * 0.00018;
    this.setScale(1 + Math.sin(time / 520 + this.x) * 0.04);
    this.label.setPosition(this.x, this.y);

    if (this.showSafety && this.indicator && this.player) {
      const pulse = 0.45 + Math.sin(time / 180) * 0.25;
      if (this.value >= this.player.strength) {
        this.setIndicator(COLORS.danger, pulse);
      } else if (this.inEngulfRange()) {
        this.setIndicator(COLORS.safe, pulse);
      } else {
        this.indicator.setVisible(false);
      }
    }
  }

  private setIndicator(color: number, pulse: number): void {
    this.indicator!
      .setVisible(true)
      .setPosition(this.x, this.y)
      .setTint(color)
      .setAlpha(pulse * 0.55)
      .setScale((this.def.radius * 2.8) / this.indicator!.width);
  }

  inEngulfRange(): boolean {
    if (!this.player) return false;
    const range = this.player.effectiveRadius + this.def.radius + ENGULF_PAD;
    return this.distanceTo(this.player.x, this.player.y) <= range;
  }

  distanceTo(x: number, y: number): number {
    return Phaser.Math.Distance.Between(this.x, this.y, x, y);
  }

  pullToward(x: number, y: number, strength: number): void {
    this.x = Phaser.Math.Linear(this.x, x, strength);
    this.y = Phaser.Math.Linear(this.y, y, strength);
  }

  /** Shoot mode: take damage. Returns true if this hit removed it. */
  damage(amount: number): boolean {
    if (this.removed) return false;
    this.value -= amount;
    if (this.value <= 0) {
      this.removed = true;
      return true;
    }
    this.label.setText(`${this.value}`);
    return false;
  }

  /** Shoot mode: pop (shrink in place), then emit GONE. */
  pop(durationMs: number): void {
    this.removed = true;
    this.indicator?.setVisible(false);
    this.scene.tweens.add({
      targets: [this, this.label],
      scale: 0.1,
      alpha: 0,
      duration: durationMs,
      ease: "Back.easeIn",
      onComplete: () => {
        this.scene.events.emit(PATHOGEN_GONE, this);
        this.destroy();
      },
    });
  }

  /** Engulf mode: shrink toward the cell, then emit GONE. */
  engulf(cell: Phaser.GameObjects.Components.Transform, durationMs: number): void {
    if (this.removed) return;
    this.removed = true;
    this.indicator?.setVisible(false);
    this.setDepth(6);
    this.scene.tweens.add({
      targets: [this, this.label],
      scale: 0.12,
      alpha: { from: 1, to: 0.6 },
      duration: durationMs,
      ease: "Sine.easeIn",
      onUpdate: () => {
        this.x = Phaser.Math.Linear(this.x, (cell as any).x, 0.15);
        this.y = Phaser.Math.Linear(this.y, (cell as any).y, 0.15);
        this.label.setPosition(this.x, this.y);
      },
      onComplete: () => {
        this.scene.events.emit(PATHOGEN_GONE, this);
        this.destroy();
      },
    });
  }

  destroy(fromScene?: boolean): void {
    this.label?.destroy();
    this.indicator?.destroy();
    super.destroy(fromScene);
  }
}
